import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./bottomTab.css";

const getDivisionFromPath = (pathname) => {
  if (pathname.startsWith("/men")) return "men";
  if (pathname.startsWith("/kids")) return "kids";
  if (pathname.startsWith("/sport")) return "sport";
  return "women";
};
/* ─── Scroll direction: returns true when the bar should be hidden ─── */
const useHideOnScrollDown = (resetKey, threshold = 10) => {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  // Always show the bar after navigating
  useEffect(() => {
    setHidden(false);
    lastY.current = window.scrollY;
  }, [resetKey]);

  useEffect(() => {
    lastY.current = window.scrollY;

    const update = () => {
      const maxY = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        0,
      );
      // clamp to ignore iOS rubber-band overscroll
      const y = Math.min(Math.max(window.scrollY, 0), maxY);
      const diff = y - lastY.current;

      if (y < 10) {
        setHidden(false); // near top → always visible
        lastY.current = y;
      } else if (Math.abs(diff) >= threshold) {
        setHidden(diff > 0); // down = hide, up = show
        lastY.current = y;
      }
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return hidden;
};
/* ─── WhatsApp Config ─── */
const WHATSAPP_PHONE = "254726327352"; // TODO: replace with your real number, digits only (country code + number)
const WHATSAPP_MESSAGE = "Hi! I have a question.";

/* ─── Modern Icon Components ─── */
const IconHome = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
    <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

const IconCatalog = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
  </svg>
);

const IconLike = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

const IconBasket = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
    <path d="M3 6h18" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const IconProfile = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconScan = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <rect width="8" height="8" x="8" y="8" rx="1" />
  </svg>
);

const IconWhatsApp = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-7.94 7.94c0 1.4.37 2.77 1.07 3.97L4 20l4.2-1.1a7.93 7.93 0 0 0 3.85 1h0a7.94 7.94 0 0 0 7.94-7.94 7.9 7.9 0 0 0-2.39-5.64zM12.05 18.4h0a6.44 6.44 0 0 1-3.29-.9l-.24-.14-2.45.64.66-2.39-.16-.25a6.44 6.44 0 0 1-1-3.42 6.46 6.46 0 0 1 6.46-6.46 6.4 6.4 0 0 1 4.57 1.9 6.4 6.4 0 0 1 1.89 4.56 6.46 6.46 0 0 1-6.44 6.46zm3.53-4.84c-.19-.1-1.15-.57-1.33-.63-.18-.07-.31-.1-.44.1-.13.19-.5.63-.62.76-.11.13-.23.14-.42.05-.19-.1-.83-.31-1.58-.98-.58-.52-.98-1.16-1.09-1.36-.11-.19-.01-.3.09-.4.09-.09.19-.23.29-.34.1-.11.13-.19.19-.32.06-.13.03-.24-.02-.34-.05-.1-.44-1.06-.6-1.45-.16-.38-.32-.33-.44-.34-.11-.01-.24-.01-.37-.01-.13 0-.34.05-.52.24-.18.19-.68.66-.68 1.62s.7 1.88.79 2.01c.1.13 1.38 2.11 3.35 2.96.47.2.83.32 1.12.41.47.15.89.13 1.23.08.38-.06 1.15-.47 1.31-.92.16-.45.16-.84.11-.92-.05-.08-.18-.13-.37-.23z" />
  </svg>
);

/* ─── Badge Component ─── */
const TabIcon = memo(function TabIcon({ icon, count }) {
  return (
    <span className="btab-icon-wrap">
      {icon}
      {count > 0 && (
        <span className="btab-badge" aria-label={`${count} items`}>
          {count > 99 ? "99+" : count}
        </span>
      )}
    </span>
  );
});

/* ─── Tab Item Component ─── */
const TabItem = memo(function TabItem({ tab, isActive, onClick }) {
  return (
    <button
      type="button"
      className={`btab-item ${isActive ? "active" : ""} ${tab.isCenter ? "center-btn" : ""}`}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      aria-label={tab.label}
    >
      <span className="btab-icon" aria-hidden="true">
        {tab.icon}
      </span>
      <span className="btab-label">{tab.label}</span>
    </button>
  );
});

/* ─── WhatsApp Floating Chat Button ─── */
const WhatsAppButton = memo(function WhatsAppButton() {
  const handleClick = () => {
    const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
      WHATSAPP_MESSAGE,
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="wa-float-container">
      <span className="wa-tooltip" role="tooltip">
        Chat with us!
        <span className="wa-tooltip-arrow" aria-hidden="true" />
      </span>
      <button
        type="button"
        className="wa-float-btn"
        onClick={handleClick}
        aria-label="Chat with us on WhatsApp"
      >
        <IconWhatsApp />
      </button>
    </div>
  );
});

/* ─── Main Bottom Navigation Bar ─── */
function BottomTabBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const hidden = useHideOnScrollDown(location.pathname);
  const user = useSelector((state) => state.user?.user);
  const wishlist = useSelector((state) => state.wishlist?.wishlist);
  const cart = useSelector((state) => state.cart?.cart);

  const isAdmin = ["admin", "manager"].includes(user?.role);
  const wishlistCount = wishlist?.length || 0;
  const cartCount = useMemo(
    () => (cart || []).reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cart],
  );

  const avatarUrl =
    typeof user?.avatar === "object" ? user?.avatar?.url : user?.avatar;

  const tabs = useMemo(
    () => [
      { key: "home", label: "Home", icon: <IconHome />, path: "/" },
      {
        key: "shop",
        label: "Shop",
        icon: <IconCatalog />,
        path: `/all-products?division=${getDivisionFromPath(location.pathname)}`,
      },
      ...(isAdmin
        ? [
            {
              key: "pos",
              label: "POS",
              icon: <IconScan />,
              path: "/pos",
              isCenter: true,
            },
          ]
        : [
            {
              key: "wishlist",
              label: "Wishlist",
              icon: <TabIcon icon={<IconLike />} count={wishlistCount} />,
              path: "/wishlist",
            },
          ]),
      {
        key: "basket",
        label: "Basket",
        icon: <TabIcon icon={<IconBasket />} count={cartCount} />,
        path: "/cart",
      },
      {
        key: "account",
        label: "Account",
        icon: avatarUrl ? (
          <img
            src={avatarUrl}
            alt={user?.name || "User Profile"}
            className="btab-avatar"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              if (e.currentTarget.parentElement) {
                e.currentTarget.parentElement.classList.add("avatar-fallback");
              }
            }}
          />
        ) : (
          <IconProfile />
        ),
        path: user ? "/dashboard" : "/login",
      },
    ],
    [isAdmin, wishlistCount, cartCount, avatarUrl, user],
  );

  const isTabActive = (tabPath) => {
    const base = tabPath.split("?")[0]; // ignore the query string
    if (base === "/") return location.pathname === "/";
    return location.pathname.startsWith(base);
  };

  return (
    <>
      <WhatsAppButton />
      <nav
        className={`btab-wrap ${hidden ? "btab-hidden" : ""}`}
        aria-label="Bottom Navigation"
      >
        {" "}
        {tabs.map((tab) => (
          <TabItem
            key={tab.key}
            tab={tab}
            isActive={isTabActive(tab.path)}
            onClick={() => navigate(tab.path)}
          />
        ))}
      </nav>
      <div className="btab-spacer" aria-hidden="true" />
    </>
  );
}

export default memo(BottomTabBar);
